package dev.lingvo.site.pages.app.settings

import androidx.compose.runtime.Composable
import dev.lingvo.site.api.Reloadable
import dev.lingvo.site.api.loadReloadable
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.state.requestWorkspaceResource
import dev.lingvo.site.i18n.t

/**
 * Actual Billing read/refusal boundary, shared by the route and its mounted authority tests.
 *
 * [refreshKey] is a reload token, not an identity key. Passed to `loadAsync` as a key — which is
 * what all three leaves in this file did — a bump discards the value *before* the request is sent,
 * so the whole page went to skeleton bars on the button the reader had just pressed and their focus
 * fell to the document. See `reloadWorkspaceProtectedResource`, which says the same thing at length.
 */
@Composable
internal fun <Billing, Invoices> BillingProtectedReadLeaf(
    context: WorkspaceRequestContext?,
    refreshKey: Int,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    billing: suspend (workspaceSlug: String) -> Billing,
    invoices: suspend (workspaceSlug: String) -> Invoices,
    content: @Composable (
        ownerClaim: WorkspaceActionClaim?,
        billing: Reloadable<Billing>,
        invoices: Reloadable<Invoices>,
    ) -> Unit,
) {
    val readClaim = WorkspaceActionClaim.capture(context, WorkspacePrivilege.BILLING_READ)
    if (readClaim == null) {
        LAlert(
            title = t("app.settings.workspace_protected_read_leaves.billing_is_managed_by_an_owner", "Billing is managed by an owner or admin"),
            // The extractor scans `title =` and not `description =`, so every refusal in this file
            // announced its heading in the reader's language and then explained itself in English.
            description = t("app.settings.workspace_protected_read_leaves.plan_invoice_and_payment_details_are", "Plan, invoice, and payment details are available to the people who manage this workspace."),
            variant = LAlertVariant.INFO,
        )
        return
    }

    val billingState = loadReloadable(readClaim.key, context?.role, "billing-overview", reloadToken = refreshKey) {
        requestWorkspaceResource(
            context = context,
            privilege = WorkspacePrivilege.BILLING_READ,
            currentContext = currentContext,
            transport = billing,
        )
    }
    val invoiceState = loadReloadable(readClaim.key, context?.role, "billing-invoices", reloadToken = refreshKey) {
        requestWorkspaceResource(
            context = context,
            privilege = WorkspacePrivilege.BILLING_READ,
            currentContext = currentContext,
            transport = invoices,
        )
    }
    content(
        WorkspaceActionClaim.capture(context, WorkspacePrivilege.OWNER_BILLING),
        billingState,
        invoiceState,
    )
}

/** Actual provider-detail read/refusal boundary, including its unsupported-provider short circuit. */
@Composable
internal fun <Providers> ProviderProtectedReadLeaf(
    context: WorkspaceRequestContext?,
    refreshKey: Int,
    available: Boolean,
    unavailable: @Composable () -> Unit,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    providers: suspend (workspaceSlug: String) -> Providers,
    content: @Composable (claim: WorkspaceActionClaim, providers: Reloadable<Providers>) -> Unit,
) {
    val claim = WorkspaceActionClaim.capture(context, WorkspacePrivilege.PROVIDERS)
    if (claim == null) {
        LAlert(
            // Its two siblings in this file both name who holds the privilege in the title, which
            // is the form every settings refusal uses. This one described the reader instead.
            title = t("app.settings.workspace_protected_read_leaves.this_is_not_yours_to_change", "Provider keys are managed by an owner or admin"),
            description = t("app.settings.workspace_protected_read_leaves.provider_keys_are_spend_so_only", "Provider keys are spend, so only owners and admins can see or set them."),
            variant = LAlertVariant.INFO,
        )
        return
    }
    if (!available) {
        unavailable()
        return
    }

    val state = loadReloadable(claim.key, context?.role, "workspace-provider", reloadToken = refreshKey) {
        requestWorkspaceResource(
            context = context,
            privilege = WorkspacePrivilege.PROVIDERS,
            currentContext = currentContext,
            transport = providers,
        )
    }
    content(claim, state)
}

/** Actual Agents list/read/refusal boundary, covering both connections and pending approvals. */
@Composable
internal fun <Agents, Approvals> AgentsProtectedReadLeaf(
    context: WorkspaceRequestContext?,
    refreshKey: Int,
    currentContext: () -> WorkspaceRequestContext? = WorkspaceRequestContext::current,
    agents: suspend (workspaceSlug: String) -> Agents,
    approvals: suspend (workspaceSlug: String) -> Approvals,
    content: @Composable (
        claim: WorkspaceActionClaim,
        agents: Reloadable<Agents>,
        approvals: Reloadable<Approvals>,
    ) -> Unit,
) {
    val claim = WorkspaceActionClaim.capture(context, WorkspacePrivilege.AGENTS)
    if (claim == null) {
        LAlert(
            title = t("app.settings.workspace_protected_read_leaves.agents_are_managed_by_an_owner", "Agents are managed by an owner or admin"),
            description = t("app.settings.workspace_protected_read_leaves.agent_credentials_spend_requests_and_activity", "Agent credentials, spend requests, and activity are not available for your role in this workspace."),
            variant = LAlertVariant.INFO,
        )
        return
    }

    val agentState = loadReloadable(claim.key, context?.role, "workspace-agents", reloadToken = refreshKey) {
        requestWorkspaceResource(
            context = context,
            privilege = WorkspacePrivilege.AGENTS,
            currentContext = currentContext,
            transport = agents,
        )
    }
    val approvalState = loadReloadable(claim.key, context?.role, "workspace-agent-approvals", reloadToken = refreshKey) {
        requestWorkspaceResource(
            context = context,
            privilege = WorkspacePrivilege.AGENTS,
            currentContext = currentContext,
            transport = approvals,
        )
    }
    content(claim, agentState, approvalState)
}
